import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { APIRequestStatus } from 'core/constants/redux';
import {
  IArchivalExportRow, IArchivalRequestRow, IArchivedRecord,
} from 'core/models/archival';
import initialArchivalState from './archival-store.initial-state';
import {
  ArchivalActionTypes,
  ArchivalView,
  IArchivalFormState,
  IArchivalSearchState,
  IArchivalState,
} from './archival-store.state';
import {
  createArchivalExport,
  createArchivalRequest,
  getArchivalExports,
  getArchivalRequests,
  getArchivedRecord,
  RECORD_LOOKUP_UNAVAILABLE,
} from './archival-store.requests';

const archivalStoreSlice = createSlice({
  name: 'archival',
  initialState: initialArchivalState,
  reducers: {
    [ArchivalActionTypes.SET_ARCHIVAL_VIEW]: (state: IArchivalState, action: PayloadAction<ArchivalView>) => {
      state.view = action.payload;
    },
    [ArchivalActionTypes.SET_ARCHIVAL_REQUEST_FORM]: (state: IArchivalState, action: PayloadAction<Partial<IArchivalFormState>>) => {
      state.requestForm = { ...state.requestForm, ...action.payload };
      state.submitError = '';
    },
    [ArchivalActionTypes.SET_ARCHIVAL_EXPORT_FORM]: (state: IArchivalState, action: PayloadAction<Partial<IArchivalFormState>>) => {
      state.exportForm = { ...state.exportForm, ...action.payload };
      state.submitError = '';
    },
    [ArchivalActionTypes.SET_ARCHIVAL_SEARCH]: (state: IArchivalState, action: PayloadAction<Partial<IArchivalSearchState>>) => {
      state.search = { ...state.search, ...action.payload };
    },
    [ArchivalActionTypes.RESET_ARCHIVAL_RECORD]: (state: IArchivalState) => {
      state.record = null;
      state.isRecordNotFound = false;
      state.isRecordLookupUnavailable = false;
      state.requestProcessing[ArchivalActionTypes.GET_RECORD_REQUEST] = APIRequestStatus.AboutToLoad;
    },
    [ArchivalActionTypes.CLEAR_ARCHIVAL_SUBMIT_ERROR]: (state: IArchivalState) => {
      state.submitError = '';
    },
  },
  extraReducers: (builder) => {
    builder.addCase(createArchivalRequest.pending, (state: IArchivalState) => {
      state.submitError = '';
      state.requestProcessing[ArchivalActionTypes.CREATE_ARCHIVAL_REQUEST] = APIRequestStatus.Processing;
    });
    builder.addCase(createArchivalRequest.fulfilled, (state: IArchivalState) => {
      state.requestProcessing[ArchivalActionTypes.CREATE_ARCHIVAL_REQUEST] = APIRequestStatus.Success;
    });
    builder.addCase(createArchivalRequest.rejected, (state: IArchivalState, action) => {
      state.submitError = (action.payload as string) ?? '';
      state.requestProcessing[ArchivalActionTypes.CREATE_ARCHIVAL_REQUEST] = APIRequestStatus.Failure;
    });

    builder.addCase(getArchivalRequests.pending, (state: IArchivalState) => {
      state.requestProcessing[ArchivalActionTypes.GET_ARCHIVAL_REQUESTS] = APIRequestStatus.Processing;
    });
    builder.addCase(getArchivalRequests.fulfilled, (state: IArchivalState, action: PayloadAction<Array<IArchivalRequestRow>>) => {
      state.requests = action.payload ?? [];
      state.requestProcessing[ArchivalActionTypes.GET_ARCHIVAL_REQUESTS] = APIRequestStatus.Success;
    });
    builder.addCase(getArchivalRequests.rejected, (state: IArchivalState) => {
      state.requestProcessing[ArchivalActionTypes.GET_ARCHIVAL_REQUESTS] = APIRequestStatus.Failure;
    });

    builder.addCase(createArchivalExport.pending, (state: IArchivalState) => {
      state.submitError = '';
      state.requestProcessing[ArchivalActionTypes.TRIGGER_EXPORT_REQUEST] = APIRequestStatus.Processing;
    });
    builder.addCase(createArchivalExport.fulfilled, (state: IArchivalState) => {
      state.requestProcessing[ArchivalActionTypes.TRIGGER_EXPORT_REQUEST] = APIRequestStatus.Success;
    });
    builder.addCase(createArchivalExport.rejected, (state: IArchivalState, action) => {
      state.submitError = (action.payload as string) ?? '';
      state.requestProcessing[ArchivalActionTypes.TRIGGER_EXPORT_REQUEST] = APIRequestStatus.Failure;
    });

    builder.addCase(getArchivalExports.pending, (state: IArchivalState) => {
      state.requestProcessing[ArchivalActionTypes.GET_EXPORT_LIST_REQUEST] = APIRequestStatus.Processing;
    });
    builder.addCase(getArchivalExports.fulfilled, (state: IArchivalState, action: PayloadAction<Array<IArchivalExportRow>>) => {
      state.exports = action.payload ?? [];
      state.requestProcessing[ArchivalActionTypes.GET_EXPORT_LIST_REQUEST] = APIRequestStatus.Success;
    });
    builder.addCase(getArchivalExports.rejected, (state: IArchivalState) => {
      state.requestProcessing[ArchivalActionTypes.GET_EXPORT_LIST_REQUEST] = APIRequestStatus.Failure;
    });

    builder.addCase(getArchivedRecord.pending, (state: IArchivalState) => {
      state.isRecordNotFound = false;
      state.isRecordLookupUnavailable = false;
      state.requestProcessing[ArchivalActionTypes.GET_RECORD_REQUEST] = APIRequestStatus.Processing;
    });
    // a null payload is the archive's own "no such record", resolved not rejected
    builder.addCase(getArchivedRecord.fulfilled, (state: IArchivalState, action: PayloadAction<IArchivedRecord>) => {
      state.record = action.payload ?? null;
      state.isRecordNotFound = !action.payload;
      state.requestProcessing[ArchivalActionTypes.GET_RECORD_REQUEST] = APIRequestStatus.Success;
    });
    builder.addCase(getArchivedRecord.rejected, (state: IArchivalState, action) => {
      state.record = null;
      state.isRecordLookupUnavailable = action.payload === RECORD_LOOKUP_UNAVAILABLE;
      state.requestProcessing[ArchivalActionTypes.GET_RECORD_REQUEST] = APIRequestStatus.Failure;
    });
  },
});

export const {
  SET_ARCHIVAL_VIEW: setArchivalView,
  SET_ARCHIVAL_REQUEST_FORM: setArchivalRequestForm,
  SET_ARCHIVAL_EXPORT_FORM: setArchivalExportForm,
  SET_ARCHIVAL_SEARCH: setArchivalSearch,
  RESET_ARCHIVAL_RECORD: resetArchivalRecord,
  CLEAR_ARCHIVAL_SUBMIT_ERROR: clearArchivalSubmitError,
} = archivalStoreSlice.actions;

export default archivalStoreSlice;
